// Phase 7a Soil Sensors page — list all sensors on the course with their
// latest reading per metric. Admin-only action: seed 54 demo sensors.
// Richer placement UI + map view land in 7b.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { SensorWithLatest, UserRole } from '@greeniq/shared';
import { ApiError } from '../api/client.js';
import { getCourseSensors, seedDemoSensors } from '../api/sensors.js';
import { useMe } from '../context/MeContext.js';

export function SensorsPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const [sensors, setSensors] = useState<SensorWithLatest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState<string | null>(null);

  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const isAdmin = role === 'ageye_admin';

  async function load() {
    if (!courseSlug) return;
    try {
      const r = await getCourseSensors(courseSlug);
      setSensors(r.sensors);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  async function onSeed() {
    if (!courseSlug) return;
    setSeeding(true);
    setSeedMessage(null);
    try {
      const r = await seedDemoSensors(courseSlug);
      setSeedMessage(
        `Seeded ${r.sensorsInserted} new sensor${r.sensorsInserted === 1 ? '' : 's'} across ${r.zonesTouched} zone${r.zonesTouched === 1 ? '' : 's'} (${r.sensorsExisting} pre-existing, skipped).`,
      );
      await load();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      setSeedMessage(`Seed failed: ${message}`);
    } finally {
      setSeeding(false);
    }
  }

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Phase 7</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">Soil Sensors</h1>
          <p className="mt-1 text-fg-muted">
            FAO-56 soil water balance simulator. Real weather drives physics-simulated VWC, soil temp, EC, and leaf wetness.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={onSeed}
              disabled={seeding}
              className="px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-50"
            >
              {seeding ? 'Seeding…' : 'Seed demo sensors'}
            </button>
          </div>
        )}
      </div>

      {seedMessage && (
        <div className="mt-4 p-3 rounded-md bg-bg-elevated text-sm text-fg">{seedMessage}</div>
      )}

      {sensors === null ? (
        <p className="mt-8 text-fg-muted">Loading…</p>
      ) : sensors.length === 0 ? (
        <div className="mt-8 p-8 text-center text-fg-muted bg-bg-card rounded-lg border border-dashed border-border">
          <p>No sensors on this course yet.</p>
          {isAdmin ? (
            <p className="mt-2 text-sm">
              Trace zones in the{' '}
              <Link to={`/app/${courseSlug}/map`} className="text-greeniq-400 hover:underline">
                Course Map
              </Link>
              , then click "Seed demo sensors" above to place the 18 greens × 2 depths + 18 fairway demo rig.
            </p>
          ) : (
            <p className="mt-2 text-sm">Ask an AGEYE admin to seed the demo rig.</p>
          )}
        </div>
      ) : (
        <div className="mt-8 overflow-auto bg-bg-card rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border bg-bg-elevated">
                <th className="px-3 py-2 font-semibold text-fg-muted">#</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">Zone</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">Type</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">Depth</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">VWC</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">Soil °F</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">EC</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">Leaf</th>
                <th className="px-3 py-2 font-semibold text-fg-muted">Last</th>
              </tr>
            </thead>
            <tbody>
              {sensors.map((s) => (
                <tr key={s.sensor.id} className="border-b border-border last:border-b-0">
                  <td className="px-3 py-2 text-fg-muted">{s.sensor.id}</td>
                  <td className="px-3 py-2">
                    {s.sensor.zoneId ? (
                      <Link
                        to={`/app/${courseSlug}/zones/${s.sensor.zoneId}`}
                        className="text-greeniq-400 hover:underline"
                      >
                        Zone {s.sensor.zoneId}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 capitalize">{s.sensor.type.replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2">
                    {s.sensor.depthInches !== null ? `${s.sensor.depthInches}″` : '—'}
                  </td>
                  <td className="px-3 py-2">{fmt(s.latest['vwc']?.value, 3)}</td>
                  <td className="px-3 py-2">{fmt(s.latest['soil_temp_f']?.value, 1)}</td>
                  <td className="px-3 py-2">{fmt(s.latest['ec_ds_m']?.value, 2)}</td>
                  <td className="px-3 py-2">
                    {s.latest['leaf_wetness']?.value === 1 ? 'Wet' : s.latest['leaf_wetness']?.value === 0 ? 'Dry' : '—'}
                  </td>
                  <td className="px-3 py-2 text-fg-muted">
                    {firstTs(s) ? relTime(firstTs(s)!) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function firstTs(s: SensorWithLatest): string | null {
  for (const info of Object.values(s.latest)) {
    if (info?.ts) return info.ts;
  }
  return null;
}

function fmt(v: number | undefined, digits: number): string {
  if (v === undefined || v === null || !Number.isFinite(v)) return '—';
  return v.toFixed(digits);
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffS = Math.floor((Date.now() - then) / 1000);
  if (diffS < 60) return `${diffS}s ago`;
  if (diffS < 3600) return `${Math.floor(diffS / 60)}m ago`;
  if (diffS < 86400) return `${Math.floor(diffS / 3600)}h ago`;
  return `${Math.floor(diffS / 86400)}d ago`;
}
